import { ServiceId, SERVICE_DATA } from './serviceData'

// -----------------------------------------------------------------------------
// Types
// -----------------------------------------------------------------------------

export type SlotKind = 'location' | 'when' | 'number' | 'text' | 'photos'

export interface Slot {
  id: string
  label: string // short label for "booking so far" panel
  kind: SlotKind
  askPhrases: string[] // variations the AI can use for asking
  suggestions?: string[] // quick-reply chip suggestions
  optional?: boolean
  unit?: string // e.g. "bedrooms", "pallets", "tons"
}

export interface ConversationTurn {
  id: number
  role: 'ai' | 'user'
  text?: string
  attachments?: { url: string; name: string }[]
  suggestions?: string[] // chip suggestions to display under this AI turn
  showUploader?: boolean // if true, show the photo uploader UI under this AI turn
  isComplete?: boolean // marks the "everything captured" turn
}

export type SlotValues = Record<string, string | number | string[]>

// -----------------------------------------------------------------------------
// Slot definitions per service
// -----------------------------------------------------------------------------

const LOC_SUGGESTIONS = [
  '📍 Use my current location',
  'Dubai Marina',
  'Downtown Dubai',
  'Business Bay',
  'JLT',
  'JVC',
  'Abu Dhabi',
  'Sharjah',
]

const TIME_SUGGESTIONS = [
  'Right now',
  'Today afternoon',
  'Tomorrow morning',
  'This weekend',
]

export const SERVICE_SLOTS: Record<ServiceId, Slot[]> = {
  carrier: [
    {
      id: 'vehicle_type',
      label: 'Vehicle',
      kind: 'text',
      askPhrases: [
        'First — what car are you moving? Make and model is enough (e.g. Toyota Camry, BMW X5).',
      ],
      suggestions: ['Sedan', 'SUV', 'Luxury car', 'Sports car'],
    },
    {
      id: 'from',
      label: 'Pickup',
      kind: 'location',
      askPhrases: [
        'Where should we collect the car from?',
        'Got it. And where are we picking it up from?',
      ],
      suggestions: LOC_SUGGESTIONS,
    },
    {
      id: 'to',
      label: 'Dropoff',
      kind: 'location',
      askPhrases: [
        'And where should we deliver it?',
        "Where's the destination?",
      ],
      suggestions: LOC_SUGGESTIONS,
    },
    {
      id: 'when',
      label: 'When',
      kind: 'when',
      askPhrases: [
        'When do you need this done? You can say a day or a specific time.',
        "What's the timing?",
      ],
      suggestions: TIME_SUGGESTIONS,
    },
  ],

  recovery: [
    {
      id: 'vehicle_info',
      label: 'Vehicle',
      kind: 'text',
      askPhrases: ['What car needs recovery? Just the make and model is fine.'],
      suggestions: ['Sedan', 'SUV', 'Luxury car'],
    },
    {
      id: 'location',
      label: 'Location',
      kind: 'location',
      askPhrases: [
        'Where is the car right now? Pin a spot or describe it.',
        "Where's the car located?",
      ],
      suggestions: [
        '📍 Use my current location',
        'Sheikh Zayed Rd',
        'Dubai Marina',
        'Business Bay',
      ],
    },
    {
      id: 'issue',
      label: 'Issue',
      kind: 'text',
      askPhrases: [
        'What happened? This helps the operator bring the right gear.',
      ],
      suggestions: [
        "Won't start",
        'Flat tire',
        'Battery dead',
        'Accident',
        'Stuck — need winch',
      ],
    },
  ],

  home: [
    {
      id: 'bedrooms',
      label: 'Bedrooms',
      kind: 'number',
      unit: 'BR',
      askPhrases: [
        'How many bedrooms is the place? This shapes truck size and helper count.',
      ],
      suggestions: ['Studio', '1', '2', '3', '4+'],
    },
    {
      id: 'from',
      label: 'Moving from',
      kind: 'location',
      askPhrases: ['Where are you moving from?'],
      suggestions: LOC_SUGGESTIONS,
    },
    {
      id: 'to',
      label: 'Moving to',
      kind: 'location',
      askPhrases: ['And where are you moving to?'],
      suggestions: LOC_SUGGESTIONS,
    },
    {
      id: 'when',
      label: 'When',
      kind: 'when',
      askPhrases: ['When do you want the move done?'],
      suggestions: TIME_SUGGESTIONS,
    },
    {
      id: 'photos',
      label: 'Photos',
      kind: 'photos',
      askPhrases: [
        "Last thing — can you share 2–3 photos of the main rooms? Carriers price more accurately with photos, and I'll auto-build an inventory for you. (Optional but recommended.)",
      ],
      optional: true,
    },
  ],

  furniture: [
    {
      id: 'items',
      label: 'Items',
      kind: 'text',
      askPhrases: [
        'What furniture are we moving? List the items (e.g. sofa, dining table, mattress).',
      ],
      suggestions: ['Sofa', 'Bed + mattress', 'Dining set', 'Wardrobe'],
    },
    {
      id: 'from',
      label: 'Pickup',
      kind: 'location',
      askPhrases: ['Where do we pick up from?'],
      suggestions: LOC_SUGGESTIONS,
    },
    {
      id: 'to',
      label: 'Dropoff',
      kind: 'location',
      askPhrases: ['And the destination?'],
      suggestions: LOC_SUGGESTIONS,
    },
    {
      id: 'when',
      label: 'When',
      kind: 'when',
      askPhrases: ['When do you need it?'],
      suggestions: TIME_SUGGESTIONS,
    },
  ],

  heavy: [
    {
      id: 'equipment',
      label: 'Equipment',
      kind: 'text',
      askPhrases: [
        'What equipment needs hauling? (e.g. excavator, bulldozer, crane)',
      ],
      suggestions: ['Excavator', 'Bulldozer', 'Forklift', 'Crane'],
    },
    {
      id: 'weight',
      label: 'Weight',
      kind: 'number',
      unit: 'tons',
      askPhrases: ['Approximate weight in tons?'],
      suggestions: ['10 tons', '20 tons', '30 tons', '40+ tons'],
    },
    {
      id: 'from',
      label: 'From',
      kind: 'location',
      askPhrases: ["Where's the equipment now?"],
      suggestions: LOC_SUGGESTIONS,
    },
    {
      id: 'to',
      label: 'To',
      kind: 'location',
      askPhrases: ['Where should we deliver it?'],
      suggestions: LOC_SUGGESTIONS,
    },
    {
      id: 'when',
      label: 'When',
      kind: 'when',
      askPhrases: ['When do you need this?'],
      suggestions: TIME_SUGGESTIONS,
    },
  ],

  chiller: [
    {
      id: 'temperature',
      label: 'Temp',
      kind: 'text',
      askPhrases: [
        'What temperature range do you need? Chilled, frozen, or deep freeze?',
      ],
      suggestions: ['Chilled +2 to +8°C', 'Frozen –18°C', 'Deep freeze –25°C'],
    },
    {
      id: 'cargo',
      label: 'Cargo',
      kind: 'text',
      askPhrases: ["What's the cargo?"],
      suggestions: ['Frozen food', 'Dairy', 'Seafood', 'Pharma'],
    },
    {
      id: 'from',
      label: 'From',
      kind: 'location',
      askPhrases: ['Pickup location?'],
      suggestions: LOC_SUGGESTIONS,
    },
    {
      id: 'to',
      label: 'To',
      kind: 'location',
      askPhrases: ['Dropoff location?'],
      suggestions: LOC_SUGGESTIONS,
    },
    {
      id: 'when',
      label: 'When',
      kind: 'when',
      askPhrases: ['When do you need it?'],
      suggestions: TIME_SUGGESTIONS,
    },
  ],

  goods: [
    {
      id: 'pallets',
      label: 'Pallets',
      kind: 'number',
      unit: 'pallets',
      askPhrases: ['How many pallets are we moving?'],
      suggestions: ['1–3', '4–6', '7–10', '10+'],
    },
    {
      id: 'from',
      label: 'From',
      kind: 'location',
      askPhrases: ['Pickup location?'],
      suggestions: LOC_SUGGESTIONS,
    },
    {
      id: 'to',
      label: 'To',
      kind: 'location',
      askPhrases: ['Dropoff location?'],
      suggestions: LOC_SUGGESTIONS,
    },
    {
      id: 'when',
      label: 'When',
      kind: 'when',
      askPhrases: ['When do you need it?'],
      suggestions: TIME_SUGGESTIONS,
    },
  ],

  construction: [
    {
      id: 'material',
      label: 'Material',
      kind: 'text',
      askPhrases: ["What's the load? (e.g. steel beams, cement, rebar)"],
      suggestions: ['Steel beams', 'Cement bags', 'Rebar', 'Aggregates'],
    },
    {
      id: 'longest',
      label: 'Longest piece',
      kind: 'text',
      askPhrases: ['Roughly how long is the longest piece?'],
      suggestions: ['Up to 6m', '6–12m', '12m+'],
    },
    {
      id: 'from',
      label: 'From',
      kind: 'location',
      askPhrases: ['Pickup site?'],
      suggestions: LOC_SUGGESTIONS,
    },
    {
      id: 'to',
      label: 'To',
      kind: 'location',
      askPhrases: ['Delivery site?'],
      suggestions: LOC_SUGGESTIONS,
    },
    {
      id: 'when',
      label: 'When',
      kind: 'when',
      askPhrases: ['When?'],
      suggestions: TIME_SUGGESTIONS,
    },
  ],

  business: [
    {
      id: 'stops',
      label: 'Stops/day',
      kind: 'number',
      unit: 'stops',
      askPhrases: ['How many drop points per day?'],
      suggestions: ['Up to 5', '5–10', '10+'],
    },
    {
      id: 'frequency',
      label: 'Frequency',
      kind: 'text',
      askPhrases: ['Is this daily, weekly, or a one-off?'],
      suggestions: ['Daily', 'Weekly', 'One-off'],
    },
    {
      id: 'from',
      label: 'Hub',
      kind: 'location',
      askPhrases: ["Where's the pickup hub?"],
      suggestions: LOC_SUGGESTIONS,
    },
  ],
}

// -----------------------------------------------------------------------------
// Parsing helpers
// -----------------------------------------------------------------------------

// Known UAE locations and aliases used to detect locations in free text.
const LOCATIONS: { canonical: string; aliases: string[] }[] = [
  { canonical: 'Dubai Marina', aliases: ['dubai marina', 'marina'] },
  { canonical: 'Downtown Dubai', aliases: ['downtown dubai', 'downtown'] },
  { canonical: 'JBR', aliases: ['jbr', 'jumeirah beach residence'] },
  { canonical: 'JLT', aliases: ['jlt', 'jumeirah lake towers'] },
  { canonical: 'JVC', aliases: ['jvc', 'jumeirah village circle'] },
  { canonical: 'Business Bay', aliases: ['business bay'] },
  { canonical: 'Al Quoz', aliases: ['al quoz', 'quoz'] },
  { canonical: 'DIP', aliases: ['dip', 'dubai investment park'] },
  {
    canonical: 'JAFZA',
    aliases: ['jafza', 'jebel ali free zone', 'jebel ali'],
  },
  { canonical: 'Deira', aliases: ['deira'] },
  { canonical: 'Mirdif', aliases: ['mirdif'] },
  { canonical: 'Silicon Oasis', aliases: ['silicon oasis', 'dso'] },
  { canonical: 'Dubai Hills', aliases: ['dubai hills'] },
  { canonical: 'Arabian Ranches', aliases: ['arabian ranches', 'ranches'] },
  { canonical: 'Palm Jumeirah', aliases: ['palm jumeirah', 'palm'] },
  { canonical: 'Jumeirah', aliases: ['jumeirah'] },
  { canonical: 'Dubai', aliases: ['dubai'] },
  { canonical: 'Abu Dhabi', aliases: ['abu dhabi', 'auh'] },
  { canonical: 'Sharjah', aliases: ['sharjah', 'shj'] },
  { canonical: 'Ajman', aliases: ['ajman'] },
  { canonical: 'Ras Al Khaimah', aliases: ['ras al khaimah', 'rak'] },
  { canonical: 'Fujairah', aliases: ['fujairah'] },
  { canonical: 'Al Ain', aliases: ['al ain'] },
  { canonical: 'Khalifa City', aliases: ['khalifa city'] },
  { canonical: 'Yas Island', aliases: ['yas island'] },
  { canonical: 'Sheikh Zayed Rd', aliases: ['sheikh zayed', 'szr', 'e11'] },
]

const CAR_BRANDS = [
  'toyota',
  'honda',
  'nissan',
  'bmw',
  'mercedes',
  'audi',
  'lexus',
  'porsche',
  'ferrari',
  'lamborghini',
  'tesla',
  'ford',
  'chevrolet',
  'jeep',
  'land rover',
  'range rover',
  'hyundai',
  'kia',
  'mazda',
  'volkswagen',
  'volvo',
  'mini',
  'bentley',
  'rolls royce',
]

const TIME_PATTERNS = [
  {
    canonical: 'Right now',
    regex: /\b(now|asap|right now|urgent|immediately)\b/i,
  },
  { canonical: 'Today', regex: /\btoday\b/i },
  { canonical: 'Tonight', regex: /\b(tonight|this evening)\b/i },
  { canonical: 'Tomorrow', regex: /\btomorrow\b/i },
  { canonical: 'This weekend', regex: /\b(weekend|saturday|sunday|friday)\b/i },
  { canonical: 'Next week', regex: /\bnext week\b/i },
  { canonical: 'In a few hours', regex: /\bin \d+ ?(hr|hour|hours)\b/i },
]

// Detects all locations in a string, in order of appearance.
function findLocationsInOrder(text: string): string[] {
  const lower = text.toLowerCase()
  const hits: { index: number; canonical: string }[] = []
  for (const loc of LOCATIONS) {
    for (const alias of loc.aliases) {
      const idx = lower.indexOf(alias)
      if (idx >= 0 && !hits.some((h) => h.canonical === loc.canonical)) {
        hits.push({ index: idx, canonical: loc.canonical })
        break
      }
    }
  }
  hits.sort((a, b) => a.index - b.index)
  return hits.map((h) => h.canonical)
}

function findTime(text: string): string | null {
  for (const t of TIME_PATTERNS) {
    if (t.regex.test(text)) return t.canonical
  }
  // Match patterns like "9am", "3 pm", "10:30"
  const timeMatch = text.match(/\b(\d{1,2})(:\d{2})?\s?(am|pm)\b/i)
  if (timeMatch) return timeMatch[0]
  return null
}

function findNumber(text: string): number | null {
  // Match standalone numbers or things like "2BR", "3 bedroom", "10 pallets"
  const m = text.match(/\b(\d+)\b/)
  if (m) return parseInt(m[1], 10)
  // Spelled-out small numbers
  const map: Record<string, number> = {
    studio: 0,
    one: 1,
    two: 2,
    three: 3,
    four: 4,
    five: 5,
    six: 6,
    seven: 7,
    eight: 8,
  }
  for (const [word, n] of Object.entries(map)) {
    if (new RegExp(`\\b${word}\\b`, 'i').test(text)) return n
  }
  return null
}

function findCarBrand(text: string): string | null {
  const lower = text.toLowerCase()
  for (const brand of CAR_BRANDS) {
    if (lower.includes(brand)) {
      // Try to capture brand + word after (e.g. "BMW X5")
      const re = new RegExp(`${brand}\\s?\\w*`, 'i')
      const m = text.match(re)
      if (m) return m[0].replace(/^\w/, (c) => c.toUpperCase())
    }
  }
  return null
}

function findEquipment(text: string): string | null {
  const lower = text.toLowerCase()
  const equipment = [
    'excavator',
    'bulldozer',
    'forklift',
    'crane',
    'loader',
    'roller',
    'grader',
    'backhoe',
  ]
  for (const eq of equipment) {
    if (lower.includes(eq)) {
      const re = new RegExp(`\\b\\d+[- ]?ton\\s?${eq}\\b`, 'i')
      const m = text.match(re)
      return (m ? m[0] : eq).replace(/^\w/, (c) => c.toUpperCase())
    }
  }
  return null
}

// -----------------------------------------------------------------------------
// Public API
// -----------------------------------------------------------------------------

// Service-specific phrasing for the universal optional photos slot.
const PHOTO_PROMPTS: Record<string, string> = {
  home: "Last thing — can you share 2–3 photos of the main rooms? Carriers price more accurately with photos, and I'll auto-build an inventory for you. (Optional but recommended.)",
  furniture:
    'Want to share photos of the items? It helps the carrier bring the right protection. (Optional.)',
  carrier:
    'A photo of the car (and any damage) is optional but helps the operator come prepared.',
  recovery:
    'A quick photo of the vehicle and surroundings helps the recovery driver. (Optional.)',
  heavy:
    'If you have photos of the equipment or load, attach them — helps with rigging. (Optional.)',
  chiller:
    'Photos of pallets / load configuration help us spec the truck. (Optional.)',
  goods: 'Pallet or load photos help carriers verify size. (Optional.)',
  construction:
    'Photos of the material or longest pieces help with truck sizing. (Optional.)',
  business: 'Want to share photos of typical loads or stops? (Optional.)',
}

const UNIVERSAL_PHOTO_SLOT: Slot = {
  id: 'photos',
  label: 'Photos',
  kind: 'photos',
  askPhrases: [
    'Want to attach a few photos? They help carriers prepare. (Optional.)',
  ],
  optional: true,
}

export function getSlotsForService(serviceId: ServiceId): Slot[] {
  const base = SERVICE_SLOTS[serviceId] || []
  // Photos slot is universally available as an optional final step.
  // If the service already defines one (e.g. home), respect that definition.
  if (base.some((s) => s.kind === 'photos')) return base
  return [
    ...base,
    {
      ...UNIVERSAL_PHOTO_SLOT,
      askPhrases: [
        PHOTO_PROMPTS[serviceId] || UNIVERSAL_PHOTO_SLOT.askPhrases[0],
      ],
    },
  ]
}

// Parse the initial natural-language prompt to pre-fill any slots we can detect.
export function parseInitialPrompt(
  prompt: string,
  serviceId: ServiceId,
): SlotValues {
  const slots = getSlotsForService(serviceId)
  const values: SlotValues = {}
  if (!prompt) return values

  const time = findTime(prompt)
  const num = findNumber(prompt)

  for (const slot of slots) {
    // Location slots are NEVER auto-filled — the AI must always ask the user
    // for pickup and dropoff (typed or shared via Google Maps).
    if (slot.kind === 'location') continue
    if (slot.kind === 'when' && time) {
      values[slot.id] = time
      continue
    }
    if (slot.kind === 'number' && num !== null) {
      values[slot.id] = num
      continue
    }
    if (slot.id === 'vehicle_type' || slot.id === 'vehicle_info') {
      const brand = findCarBrand(prompt)
      if (brand) values[slot.id] = brand
      continue
    }
    if (slot.id === 'equipment') {
      const eq = findEquipment(prompt)
      if (eq) values[slot.id] = eq
      continue
    }
  }

  return values
}

// Parse a free-text user reply in the context of the slot we're currently asking about.
// Falls back to using the raw text so the user always feels heard.
export function parseUserReply(reply: string, slot: Slot): SlotValues {
  const trimmed = reply.trim()
  if (!trimmed) return {}
  const values: SlotValues = {}

  switch (slot.kind) {
    case 'location': {
      const locs = findLocationsInOrder(trimmed)
      if (locs.length >= 2) {
        // "Marina to Downtown" — fill this slot and try to fill the next location slot if there is one.
        values[slot.id] = locs[0]
        values['__bonus_location__'] = locs[1] // consumer can pick up the bonus
      } else if (locs.length === 1) {
        values[slot.id] = locs[0]
      } else {
        values[slot.id] = trimmed
      }
      return values
    }
    case 'when': {
      const t = findTime(trimmed)
      values[slot.id] = t || trimmed
      return values
    }
    case 'number': {
      const n = findNumber(trimmed)
      if (n !== null) {
        values[slot.id] = n
      } else {
        // Accept words like "studio" → 0
        values[slot.id] = trimmed
      }
      return values
    }
    case 'text':
    default:
      values[slot.id] = trimmed
      return values
  }
}

// Find the next missing required slot (skips optional slots that are still empty).
export function nextMissingSlot(
  values: SlotValues,
  slots: Slot[],
): Slot | null {
  for (const slot of slots) {
    if (slot.optional) continue
    if (values[slot.id] === undefined || values[slot.id] === '') return slot
  }
  // After required slots, surface the first unfilled optional slot once.
  for (const slot of slots) {
    if (slot.optional && values[slot.id] === undefined) return slot
  }
  return null
}

export function allRequiredFilled(values: SlotValues, slots: Slot[]): boolean {
  return slots.every(
    (slot) =>
      slot.optional ||
      (values[slot.id] !== undefined && values[slot.id] !== ''),
  )
}

// Friendly acknowledgement varying by slot kind so it doesn't feel canned.
export function formatAcknowledgement(
  slot: Slot,
  value: string | number | string[],
): string {
  const v = Array.isArray(value) ? value.join(', ') : String(value)
  switch (slot.kind) {
    case 'location':
      return `${v} — got it.`
    case 'when':
      return `${v} — noted.`
    case 'number':
      return slot.unit ? `${v} ${slot.unit} — got it.` : `${v} — got it.`
    case 'photos':
      return `Photo received.`
    case 'text':
    default:
      return `Got it — "${v}".`
  }
}

export function getInitialGreeting(
  serviceId: ServiceId,
  prompt: string,
  prefilled: SlotValues,
  slots: Slot[],
): string {
  const service = SERVICE_DATA[serviceId]
  const filledCount = Object.keys(prefilled).filter(
    (k) => !k.startsWith('__'),
  ).length
  const totalRequired = slots.filter((s) => !s.optional).length

  if (filledCount === 0) {
    return `I'd like to set up your ${service.name.toLowerCase()} booking. I'll just need a few details to find the right carrier — this will take about 30 seconds.`
  }

  const filledLabels = slots
    .filter((s) => prefilled[s.id] !== undefined)
    .map((s) => `${s.label.toLowerCase()}: ${prefilled[s.id]}`)
    .slice(0, 3)
    .join(', ')

  if (filledCount >= totalRequired) {
    return `I caught all the key details from your message (${filledLabels}). Let me confirm everything below.`
  }

  return `I caught some of it — ${filledLabels}. Let me grab the rest.`
}

export function getCompletionMessage(serviceId: ServiceId): string {
  const service = SERVICE_DATA[serviceId]
  return `Perfect — I have everything I need. I've prepared your ${service.name.toLowerCase()} brief on the right. Ready to see available carriers?`
}
